/* Panel de inicio del estudiante: los tres accesos grandes (WSS Live,
   Conjugación y Recursos). Lo ve igual quien entra
   con el pase y quien está matriculado. Acabado elegido por la dueña el
   27/10/2026: tarjeta blanca con foto arriba y el color como acento. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { todayISO, daysAgoISO, formatDMY } from "@/lib/dateFormat";
import { ArrowUpRight, Radio, Sparkles, BookOpen, Gamepad2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Foto de cabecera de cada acceso: las mismas de la landing de WSS GO, para que
// el estudiante vea el mismo concepto adentro que el que lo trajo.
const WP = "https://wespeakspanish-cr.com/wp-content/uploads/2026/10";
const FOTO_CARD = {
  live: `${WP}/foto1.2.png`,
  conj: `${WP}/foto1.4.png`,
  res: `${WP}/foto1.1.png`,
  game: `${WP}/foto1.5.png`,
};

const fotoUrl = (p) => `${API_BASE}/api/wall/photos/${p.id}/image`;

// "17:00" → "5:00 p.m." (como lo lee la gente en Costa Rica).
const hora12 = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (Number.isNaN(h)) return "";
  const suf = h >= 12 ? "p.m." : "a.m.";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${suf}`;
};

const AccessCard = ({ to, icon: Icon, title, sub, pill, img, acento, chip, barra, testId }) => (
  <Link to={to} data-testid={testId}
        className="group bg-white rounded-[28px] border border-slate-200 overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-slate-900/5 hover:border-slate-300">
    <div className="relative h-40 overflow-hidden">
      <img src={img} alt="" loading="lazy"
           className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <span className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
    </div>
    <div className="px-6 pb-6 flex-1">
      <span className={`-mt-8 relative inline-grid h-14 w-14 place-items-center rounded-2xl bg-white shadow-md ring-1 ring-slate-100 ${acento}`}>
        <Icon className="h-6 w-6" />
      </span>
      <div className="mt-4 flex items-start justify-between gap-2">
        <div className="font-display text-xl font-bold tracking-tight text-slate-900">{title}</div>
        <ArrowUpRight className="h-5 w-5 text-slate-300 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
      <p className="text-sm text-slate-500 mt-1">{sub}</p>
      {pill && <span className={`mt-4 inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${chip}`}>{pill}</span>}
    </div>
    <div className={`h-1 w-0 ${barra} transition-all duration-300 group-hover:w-full`} />
  </Link>
);

/** Los tres accesos grandes, con el dato vivo de cada uno. */
export const StudentAccessCards = () => {
  const { t, lang } = useI18n();
  const [live, setLive] = useState(null);     // próxima sesión publicada
  const [verbos, setVerbos] = useState(null); // respuestas de conjugación
  const [recursos, setRecursos] = useState(null);
  const [juegos, setJuegos] = useState(null);   // juegos disponibles y partidas propias

  useEffect(() => {
    api.get("/live-classes/available").then(({ data }) => setLive((data.sessions || [])[0] || null)).catch(() => {});
    api.get("/conjugation/practice/history", { params: { limit: 100 } })
      .then(({ data }) => setVerbos(data.answered || 0)).catch(() => {});
    api.get("/resources").then(({ data }) => setRecursos((data || []).length)).catch(() => {});
    api.get("/game-zone/overview").then(({ data }) => setJuegos({
      total: (data.categories || []).reduce((n, c) => n + (c.game_count || 0), 0),
      plays: data.total_plays || 0,
    })).catch(() => {});
  }, []);

  let livePill = t("sa_live_none");
  if (live) {
    const hoy = todayISO();
    const hora = hora12(live.start_time);
    if (live.date === hoy) livePill = t("sa_live_today", { time: hora });
    else if (live.date === daysAgoISO(-1)) livePill = t("sa_live_tomorrow", { time: hora });
    else livePill = `${formatDMY(live.date, lang)} · ${hora}`;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="student-access-cards">
      <AccessCard
        testId="access-card-live" to="/dashboard/live-classes" icon={Radio}
        title={t("live_classes")} sub={t("sa_live_sub")} pill={livePill}
        img={FOTO_CARD.live}
        acento="text-[#004aad]" chip="bg-blue-50 text-blue-700" barra="bg-[#004aad]"
      />
      <AccessCard
        testId="access-card-conjugation" to="/dashboard/conjugation" icon={Sparkles}
        title={t("sa_conj_title")} sub={t("sa_conj_sub")}
        pill={verbos ? t("sa_conj_verbs", { n: verbos }) : t("sa_conj_none")}
        img={FOTO_CARD.conj}
        acento="text-[#ff731d]" chip="bg-orange-50 text-orange-700" barra="bg-[#ff731d]"
      />
      <AccessCard
        testId="access-card-resources" to="/dashboard/resources" icon={BookOpen}
        title={t("resources")} sub={t("sa_res_sub")}
        pill={recursos ? t("sa_res_count", { n: recursos }) : null}
        img={FOTO_CARD.res}
        acento="text-teal-700" chip="bg-teal-50 text-teal-700" barra="bg-teal-600"
      />
      <AccessCard
        testId="access-card-games" to="/dashboard/game-zone" icon={Gamepad2}
        title={t("game_zone")} sub={t("sa_game_sub")}
        pill={juegos?.plays ? t("sa_game_plays", { n: juegos.plays })
          : (juegos?.total ? t("sa_game_count", { n: juegos.total }) : null)}
        img={FOTO_CARD.game}
        acento="text-rose-600" chip="bg-rose-50 text-rose-700" barra="bg-rose-500"
      />
    </div>
  );
};

